import * as React from "react";
import { Context } from "@grandvisionhq/carts-v2";
import { toast, ScrollIntoView } from "@grandvisionhq/common";
import { H2, H3, Alert } from "@grandvisionhq/iris-ui";
import { PaymentMethod, PaymentServiceProvider, PaymentFlow, PaypalPaymentEventsProvider, PaymentMethodsUI } from "@grandvisionhq/payments";
import { useIntl } from "@grandvisionhq/state";
import { pushDonationSelectEventToDataLayer } from "../../../../../../data-layer.js";
import { LoaderModal } from "../../../../../../modules/LoaderModal/modules/LoaderModal/LoaderModal.js";
import { useHandleApplePay } from "../../../../../hooks/LoginExpressCheckout/hooks/useHandleApplePay.js";
import { useHandleGpayConfig } from "../../../../../hooks/LoginExpressCheckout/hooks/useHandleGpayConfig.js";
import { useHandlePaypalConfig } from "../../../../../hooks/LoginExpressCheckout/hooks/useHandlePaypalConfig.js";
import { useHandleOrderCreation } from "../../../../../hooks/LoginExpressCheckout/hooks/useHandlerOrderCreation.js";
import { useValidateAndSetOrderDetails } from "../../../../../hooks/LoginExpressCheckout/hooks/useValidateAndSetOrderDetails.js";
import { useGetQueryParams } from "../../../../../../utils/utils/get-query-params.js";
import { useHandleQueryParams } from "../../../../../../StepPayment/hooks/StepPayment/hooks/use-handle-query-params.js";
import { useLoggedInCheckout } from "../../../../../context/LoginExpressCheckout/context/context.js";
import { useHandleQrFormUpdates } from "../../../../../../StepPayment/hooks/StepPayment/hooks/use-handle-qr-form-updates.js";
import { CheckoutStepErrorType } from "../../../../../../models/checkout/models/checkout/checkout-step-error-type.js";
import { useCheckoutSettings } from "../../../../../../context/CheckoutSettings/context/CheckoutSettings/context.js";
const PaymentsContainer = ({ selfUrl, updateCheckoutStatus }) => {
  const { status, cartId, type: paymentType } = useGetQueryParams();
  const [isFailedCheckoutStep, setIsFailedCheckoutStep] = React.useState(false);
  const [updateUrlQueryParam, deleteQueryParam] = useHandleQueryParams();
  const { cart, loading } = Context.useCart();
  const { getLabel } = useIntl();
  const {
    selectedShippingMethod,
    consents,
    isSaveDetailsInAccount,
    isOrderInProgress,
    setIsOrderInProgress
  } = useLoggedInCheckout();
  const [paymentDetailsState, setPaymentDetailsState] = React.useState(void 0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState();
  const [donationInfo, setDonationInfo] = React.useState();
  const paypalAndGpayInputProp = React.useMemo(
    () => ({
      selfUrl,
      selectedPaymentMethod,
      donationInfo,
      selectedShippingMethod,
      consents,
      isSaveDetailsInAccount,
      setIsOrderInProgress
    }),
    [
      consents,
      donationInfo,
      isSaveDetailsInAccount,
      selectedPaymentMethod,
      selectedShippingMethod,
      selfUrl,
      setIsOrderInProgress
    ]
  );
  const { paypalPaymentEvents, handlePaypalCtaClick, onPaypalError } = useHandlePaypalConfig(paypalAndGpayInputProp);
  const { paymentMethodLogos } = useCheckoutSettings();
  const { handlePlaceOrder, orderResponse, paymentFlow } = useHandleOrderCreation();
  const { handleGpayCtaClick, onGpayAuthorized } = useHandleGpayConfig(paypalAndGpayInputProp);
  const { payWithApplePay } = useHandleApplePay({
    selfUrl,
    donationInfo,
    consents,
    isSaveDetailsInAccount,
    setIsOrderInProgress
  });
  const PaymentMethodsWithPopUpScreen = React.useMemo(
    () => [PaymentMethod.PAYPAL, PaymentMethod.GOOGLE_PAY, PaymentMethod.APPLE_PAY],
    []
  );
  const handleCreateOrder = React.useCallback(() => {
    if (isFailedCheckoutStep) setIsFailedCheckoutStep(false);
    const isAdyenPaypal = selectedPaymentMethod?.type === PaymentMethod.PAYPAL && selectedPaymentMethod.provider == PaymentServiceProvider.ADYEN;
    if (paymentDetailsState && selectedPaymentMethod?.type && (isAdyenPaypal || !PaymentMethodsWithPopUpScreen.includes(selectedPaymentMethod.type))) {
      handlePlaceOrder({
        selfUrl,
        selectedPaymentMethod,
        donationInfo,
        paymentDetails: paymentDetailsState,
        consents,
        isSaveDetailsInAccount,
        setIsOrderInProgress
      });
    }
  }, [
    PaymentMethodsWithPopUpScreen,
    consents,
    donationInfo,
    handlePlaceOrder,
    isFailedCheckoutStep,
    isSaveDetailsInAccount,
    paymentDetailsState,
    selectedPaymentMethod,
    selfUrl,
    setIsOrderInProgress
  ]);
  const { saveCustomerInformation } = useValidateAndSetOrderDetails({
    onComplete: handleCreateOrder
  });
  const paymentMethodType = selectedPaymentMethod?.type ?? PaymentMethod.SWISH;
  const [qrCodeFormData] = useHandleQrFormUpdates({
    ...orderResponse ? {
      qrPaymentConfig: {
        qrCodeExpiresIn: orderResponse.qrCodeExpiresIn,
        qrCodeUrl: orderResponse.qrCodeUrl,
        redirectUrl: orderResponse.redirectUrl
      }
    } : {},
    paymentFlow,
    selfUrl,
    paymentMethodType,
    paymentMethodLogos,
    orderId: paymentFlow === PaymentFlow.QR_CODE ? orderResponse?.orderId : void 0,
    orderDataToken: orderResponse?.orderDataToken
  });
  const isPaymentStepValid = React.useCallback((paymentDetails) => {
    const isValid = paymentDetails && paymentDetails.isValid;
    if (!isValid) {
      toast.failed("checkout.errors.form.payment.details");
    }
    return Boolean(isValid);
  }, []);
  const resetPaymentStatus = React.useCallback(() => {
    if (selectedPaymentMethod?.type && status !== "") {
      updateCheckoutStatus?.("");
      deleteQueryParam(["status"]);
      setIsFailedCheckoutStep(false);
    }
  }, [deleteQueryParam, selectedPaymentMethod?.type, status, updateCheckoutStatus]);
  const handleNextClick = React.useCallback(
    async ({ paymentDetailsConfig } = {}) => {
      if (isPaymentStepValid(paymentDetailsConfig)) {
        setPaymentDetailsState(paymentDetailsConfig);
        updateCheckoutStatus?.("");
        resetPaymentStatus();
        if (selectedPaymentMethod?.type === PaymentMethod.APPLE_PAY) {
          payWithApplePay({ selectedShippingMethod, consents });
        } else {
          setIsOrderInProgress(true);
          await saveCustomerInformation(selectedShippingMethod, consents).then(({ success }) => {
            if (!success) {
              setIsOrderInProgress(false);
            }
          }).catch(() => setIsOrderInProgress(false));
        }
      }
    },
    [
      consents,
      isPaymentStepValid,
      payWithApplePay,
      resetPaymentStatus,
      saveCustomerInformation,
      selectedPaymentMethod?.type,
      selectedShippingMethod,
      setIsOrderInProgress,
      updateCheckoutStatus
    ]
  );
  const handleOnPaymentMethodSelected = React.useCallback(
    (paymentMethodConfig) => {
      setDonationInfo(void 0);
      setSelectedPaymentMethod(paymentMethodConfig);
      updateUrlQueryParam({ type: paymentMethodConfig.type });
      if (selectedPaymentMethod?.type && selectedPaymentMethod.type !== paymentMethodConfig.type && status !== "") {
        updateCheckoutStatus?.("");
        deleteQueryParam(["status"]);
      }
    },
    [
      deleteQueryParam,
      selectedPaymentMethod?.type,
      status,
      updateCheckoutStatus,
      updateUrlQueryParam
    ]
  );
  const onHandlePayment = React.useCallback(
    (selectedPaymentConfig) => {
      selectedPaymentConfig && updateUrlQueryParam({ type: selectedPaymentConfig.type });
      handleNextClick({ paymentDetailsConfig: selectedPaymentConfig });
    },
    [updateUrlQueryParam, handleNextClick]
  );
  const handleDonation = React.useCallback(
    (donationPayload) => {
      pushDonationSelectEventToDataLayer(donationPayload.foundationKey, donationPayload.amount);
      selectedPaymentMethod && setDonationInfo({
        key: donationPayload.foundationKey,
        amount: donationPayload.amount,
        paymentMethod: selectedPaymentMethod?.type
      });
    },
    [selectedPaymentMethod]
  );
  React.useEffect(() => {
    if (status && [
      CheckoutStepErrorType.Failed,
      CheckoutStepErrorType.Cancelled,
      CheckoutStepErrorType.Refused,
      CheckoutStepErrorType.Error,
      CheckoutStepErrorType.InternalError
    ].includes(status)) {
      if (!loading) {
        setIsFailedCheckoutStep(true);
      }
    }
  }, [status, loading]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    LoaderModal,
    {
      isModalOpen: isOrderInProgress,
      subheading: getLabel("checkout.order.inprogress")
    }
  ), /* @__PURE__ */ React.createElement(H2, { variant: "heading-small" }, getLabel("checkout.instant.payment.heading")), /* @__PURE__ */ React.createElement(H3, { variant: "text-body" }, getLabel("checkout.instant.payment.subheading")), isFailedCheckoutStep && /* @__PURE__ */ React.createElement(ScrollIntoView, { key: "payment-error" }, /* @__PURE__ */ React.createElement(
    Alert,
    {
      role: "alert",
      "aria-describedby": "payment-alert",
      className: "alert-m-default ",
      variant: "warning",
      title: getLabel("checkout.errors.general.title")
    },
    getLabel("checkout.errors.general.message")
  )), /* @__PURE__ */ React.createElement(
    PaypalPaymentEventsProvider,
    {
      ...paypalPaymentEvents,
      onClick: handlePaypalCtaClick,
      orderType: cart?.hasSubscription ? "subscription" : "regular",
      onError: onPaypalError
    },
    /* @__PURE__ */ React.createElement(
      PaymentMethodsUI,
      {
        cartId,
        qrCodeForm: qrCodeFormData,
        onPaymentMethodSelected: handleOnPaymentMethodSelected,
        selectedDonation: donationInfo?.amount,
        onDonationSelected: handleDonation,
        paymentMethodName: paymentType ?? "",
        onHandlePayment,
        isOrderInProgress: false,
        totalPrice: cart?.prices.totalPrice,
        googlePayActions: { handleGpayCtaClick, onAuthorize: onGpayAuthorized }
      }
    )
  ));
};
export {
  PaymentsContainer
};
//# sourceMappingURL=PaymentsContainer.js.map
